'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

/**
 * Componente PasswordInput Reutilizable
 * - Toggle de visibilidad (ojo)
 * - Indicador de fuerza
 * - Mensajes de error
 * - Accesible (aria-labels)
 */
export default function PasswordInput({
  value,
  onChange,
  placeholder,
  error = null,
  label = null,
  required = false,
  autoComplete = 'current-password',
  className = '',
  showStrengthIndicator = true,
  minStrength = 'weak', // 'weak' | 'moderate' | 'strong'
  disabled = false
}) {
  const t = useTranslations('PasswordInput');
  const [showPassword, setShowPassword] = useState(false);

  // Calcular fuerza de contraseña
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { level: 'empty', label: '', color: 'text-slate-500', bg: 'bg-slate-700' };
    
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) strength++;

    if (strength <= 1) return { level: 'weak', label: t('strengthVeryWeak'), color: 'text-red-400', bg: 'bg-red-500/30' };
    if (strength <= 2) return { level: 'fair', label: t('strengthWeak'), color: 'text-orange-400', bg: 'bg-orange-500/30' };
    if (strength <= 3) return { level: 'moderate', label: t('strengthModerate'), color: 'text-yellow-400', bg: 'bg-yellow-500/30' };
    if (strength <= 4) return { level: 'strong', label: t('strengthStrong'), color: 'text-green-400', bg: 'bg-green-500/30' };
    return { level: 'very-strong', label: t('strengthVeryStrong'), color: 'text-emerald-400', bg: 'bg-emerald-500/30' };
  };

  const strength = getPasswordStrength(value);
  const strengthBars = ['weak', 'fair', 'moderate', 'strong', 'very-strong'];
  const currentStrengthIndex = strengthBars.indexOf(strength.level);

  return (
    <div className={className}>
      {label && (
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          className={`
            w-full px-4 py-3.5 pr-12 rounded-xl 
            bg-[#0F172A] text-white caret-cyan-300
            border transition-all duration-200
            focus:outline-none font-medium
            placeholder:text-slate-500
            disabled:opacity-50 disabled:cursor-not-allowed
            [color-scheme:dark]
            [&:-webkit-autofill]:[-webkit-text-fill-color:#fff]
            [&:-webkit-autofill]:[transition:background-color_9999s_ease-in-out_0s]
            ${error 
              ? 'border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' 
              : 'border-slate-800 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20'
            }
          `}
          aria-label={label || t('inputAriaLabel')}
          aria-invalid={!!error}
          aria-describedby={error ? 'password-error' : undefined}
        />
        
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          disabled={disabled}
          className={`
            absolute inset-y-0 right-0 flex items-center pr-4
            text-slate-500 transition-colors
            ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:text-cyan-400 cursor-pointer'}
          `}
          aria-label={showPassword ? t('hidePasswordAriaLabel') : t('showPasswordAriaLabel')}
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Indicador de fuerza */}
      {showStrengthIndicator && value && (
        <div className="mt-2">
          <div className="flex gap-1 mb-1.5">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className={`
                  h-1.5 flex-1 rounded-full transition-colors
                  ${idx <= currentStrengthIndex ? strength.bg : 'bg-slate-700'}
                `}
              />
            ))}
          </div>
          <p className={`text-xs font-bold ${strength.color}`}>
            {strength.label}
          </p>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <p className="text-xs text-red-400 mt-2 flex items-center gap-1" id="password-error">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </p>
      )}
      
      {/* Requisitos de seguridad (útil para usuarios) */}
      {value && !error && (
        <div className="mt-2 text-xs text-slate-400 space-y-1">
          <p className={value.length >= 8 ? 'text-green-400' : 'text-slate-500'}>
            {value.length >= 8 ? '✓' : '○'} {t('requirementMinChars')}
          </p>
          <p className={/[A-Z]/.test(value) ? 'text-green-400' : 'text-slate-500'}>
            {/[A-Z]/.test(value) ? '✓' : '○'} {t('requirementUppercase')}
          </p>
          <p className={/[0-9]/.test(value) ? 'text-green-400' : 'text-slate-500'}>
            {/[0-9]/.test(value) ? '✓' : '○'} {t('requirementNumber')}
          </p>
          <p className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ? 'text-green-400' : 'text-slate-500'}>
            {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value) ? '✓' : '○'} {t('requirementSymbol')}
          </p>
        </div>
      )}
    </div>
  );
}



